import * as React from 'react';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {SettingsCard} from '../layouts/SettingsCard';
import {useAuth} from '../../providers/AuthProvider';
import {deleteAccount} from '../../services/authService';
import {apiErrorMessage, isNetworkError} from '../../utilities/apiError';

// Deleting your own account, with everything it owns. Asks for the password again (a temporary account has none), says what will go,
// and cannot be undone.
export function DeleteAccountCard({temporary, hasPassword = true}: { temporary: boolean, hasPassword?: boolean }) {
    // An account made with Google or Apple has no password to give; the server wants a sign-in from the last few minutes instead.
    const needsPassword = !temporary && hasPassword;
    const [open, setOpen] = React.useState(false);
    const [password, setPassword] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {logoutWithAuthProvider} = useAuth();

    const close = () => { if (!busy) { setOpen(false); setPassword(''); setError(''); } };

    const confirm = async () => {
        setBusy(true);
        setError('');
        try {
            await deleteAccount(needsPassword ? password : undefined);
            // Whatever was cached belonged to the account that is gone.
            queryClient.clear();
            logoutWithAuthProvider();
            navigate('/login', {replace: true});
        } catch (failure) {
            setError(isNetworkError(failure) ? 'Network error. Please try again later.' : apiErrorMessage(failure, "The account couldn't be deleted. Try again."));
            setBusy(false);
        }
    };

    return (
        <SettingsCard title="Delete account" description="Remove your account and everything in it">
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                This permanently deletes your account, the boards you own (with their tasks and photos), your place on other people&apos;s boards,
                and your notifications and email settings. It cannot be undone.
            </Typography>
            <Button color="error" variant="outlined" onClick={() => setOpen(true)}>Delete my account…</Button>

            <Dialog open={open} onClose={close} aria-labelledby="delete-account-title" fullWidth maxWidth="xs">
                <DialogTitle id="delete-account-title">Delete your account?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        Everything you own will be deleted for good, including boards other people share with you as their owner.
                        {needsPassword ? ' To make sure it is you, enter your password.' : ''}
                        {!temporary && !hasPassword ? ' This account signs in with Google or Apple, so it has no password: if you signed in more than a few minutes ago, you will be asked to sign in again first.' : ''}
                    </DialogContentText>
                    {needsPassword && (
                        <TextField
                            autoFocus fullWidth type="password" label="Password" autoComplete="current-password"
                            value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy}
                            onKeyDown={(e) => { if (e.key === 'Enter' && password && !busy) void confirm(); }}
                        />
                    )}
                    {error && <Alert severity="error" sx={{mt: 2}}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Box sx={{flexGrow: 1}}/>
                    <Button onClick={close} disabled={busy}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => void confirm()} disabled={busy || (needsPassword && !password)}>
                        Delete everything
                    </Button>
                </DialogActions>
            </Dialog>
        </SettingsCard>
    );
}
